import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import { getRelativeDateLocalization } from '../../../helpers/localization/localizationUtils';
import { modifiers, isPastDate } from '../../../helpers/dayCard/dayCard';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Icon from '../../elements/Icon';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemMiles from '../_commons/atoms/FareItemMiles';
import FareItemNoPriceAvailable from '../_commons/atoms/FareItemNoPriceAvailable';
import { handleFareItemInteraction } from '../_commons/commonFns';
import ErrorBoundary from '../../utils/ErrorBoundary';
import SkeletonCalendarCard from '../../others/Skeleton/SkeletonCalendarCard';
import { Tooltip } from 'react-tooltip';

const KEY_ENTER = 13;
const KEY_SPACE = 32;
const DayCardMobile = (props) => {
  const {
    day,
    showPrice,
    showMiles,
    loading,
    usedErrorBoundary,
    disregardedBehavior,
    isDisregarded,
    isOutside,
    handlerDayClick,
    showAsEmptyDay = false,
    monthShow,
    selectedStartDate,
    selectedEndDate,
    highlightLowestPrices,
    departureFare,
    returnFare,
    isInDateRange,
    isBlackoutDay,
    isToday,
    confirmedDate,
    direction,
    sameDepartureAndReturnDate,
    departureDateLtrStyle,
    departureDateRtlStyle,
    returnDateLtrStyle,
    returnDateRtlStyle,
    selectedDay,
    combinable,
    dayCardDaySkeletonStyle,
    dayCardDaySkeletonCombinableStyle,
    accessibleByArrowKeys,
    dayCardTooltipSettings = {},
    cardContentSettings = {},
    handlerDayHover,
    highlightDeparture,
    islastHoveredDate,
    hoverLastDateLtrStyle = {},
    hoverLastDateRtlStyle = {},
    focusDate,
    focusReferences,
    tabIndex,
    handleKeyboardNavigation,
    isOneWayTrip,
    isSelectedStartDate,
    showDayEmpty
  } = props;
  let data = props.data;
  let isLowest = props.isLowest;
  if (loading && !isOutside && !isDisregarded && !isPastDate(day)) {
    if (selectedStartDate && departureFare) {
      data = departureFare.fareItem;
      isLowest = departureFare.isLowest;
    } else if (selectedEndDate && returnFare) {
      data = returnFare.fareItem;
      isLowest = returnFare.isLowest;
    } else if (selectedDay && returnFare) {
      data = returnFare.fareItem;
      isLowest = returnFare.isLowest;
    }
  }

  const [isPressed, setIsPressed] = React.useState(false);
  const isASelectedDay = selectedStartDate || selectedEndDate || selectedDay;
  const isDayActiveInMonth = !isOutside && !isPastDate(day);
  let dayCardMobile = modifiers({
    ...props,
    loadingStatus: loading && isDayActiveInMonth,
    applyLoadingStyle: combinable && loading && !isOutside
  });
  const { localizationData, translate } = useContext(Context);

  if (isLowest && highlightLowestPrices) {
    dayCardMobile = {
      ...dayCardMobile,
      dayCardMobilePrice: {
        ...dayCardMobile.dayCardMobilePrice,
        currencyStyles: dayCardMobile.dayCardMobilePrice?.currencySheapStyles
      },
      dayCardMobileMiles: {
        ...dayCardMobile.dayCardMobileMiles,
        amountStyles: dayCardMobile.dayCardMobileMiles?.amountSheapStyles,
        taxAmountStyles: dayCardMobile.dayCardMobileMiles?.taxAmountSheapStyles
      }
    };
  }
  let lowestCircleStyle = { ...dayCardMobile.lowestCircleStyle };
  if (isLowest && highlightLowestPrices && (isASelectedDay || isPressed)) {
    lowestCircleStyle = { ...dayCardMobile.lowestCircleSelectedStyle };
  }

  let dataTestId = 'dayCard';
  if (isOutside) {
    dataTestId = 'dayCard--outside';
  }
  if (isDisregarded) {
    dataTestId = 'dayCard--disregarded';
  }
  if (isPastDate(day)) {
    dataTestId = 'dayCard--pastdate';
  }
  const isSameDay = dayjs(day).isSame(focusDate, 'day');
  let isInteractive =
    accessibleByArrowKeys &&
    !(isPastDate(day) || isDisregarded || isOutside) &&
    isSameDay &&
    !props.isEmptyCard;
  isInteractive = props.isEmptyCard ? false : isInteractive;
  const [isFocused, setIsFocused] = React.useState(false);
  const { handleCallToAction, handleCallToActionKeyDown } = handleFareItemInteraction(props);

  const renderDayAtom = (day, dayCardMobile) => {
    return (
      dayCardMobile.showDay && (
        <Text
          {...dayCardMobile?.dayCardMobileDay}
          role="heading"
          ariaLevel="3"
          ariaLabel={dayjs(day)
            .locale(getRelativeDateLocalization(translate, localizationData), null, true)
            .format('MMMM D')}
        >
          {dayjs(day).date()}
        </Text>
      )
    );
  };
  const renderCardContent = () => {
    if (loading) {
      if (combinable) {
        if (isDayActiveInMonth && (selectedStartDate || selectedEndDate || selectedDay)) {
          return (
            <>
              <Text
                {...dayCardMobile?.dayCardMobileDay}
                role="heading"
                ariaLevel="3"
                ariaLabel={dayjs(day)
                  .locale(getRelativeDateLocalization(translate, localizationData), null, true)
                  .format('MMMM D')}
              >
                {dayjs(day).date()}
              </Text>
              {!props.dayBeforeDeparture && (
                <SkeletonCalendarCard
                  showExtendedLayout={true}
                  dayCardDaySkeletonStyle={dayCardDaySkeletonCombinableStyle}
                />
              )}
            </>
          );
        } else if (isDayActiveInMonth && !selectedStartDate && !selectedEndDate && !selectedDay) {
          return (
            <>
              <Text
                {...dayCardMobile?.dayCardMobileDay}
                role="heading"
                ariaLevel="3"
                ariaLabel={dayjs(day)
                  .locale(getRelativeDateLocalization(translate, localizationData), null, true)
                  .format('MMMM D')}
              >
                {dayjs(day).date()}
              </Text>
              {!props.dayBeforeDeparture && (
                <SkeletonCalendarCard
                  showExtendedLayout={true}
                  dayCardDaySkeletonStyle={dayCardDaySkeletonStyle}
                />
              )}
            </>
          );
        }
      } else {
        if (
          isDayActiveInMonth &&
          !selectedStartDate &&
          !selectedEndDate &&
          !selectedDay &&
          !isPastDate(day) &&
          !isDisregarded
        ) {
          return (
            <>
              <Text
                {...dayCardMobile?.dayCardMobileDay}
                role="heading"
                ariaLevel="3"
                ariaLabel={dayjs(day)
                  .locale(getRelativeDateLocalization(translate, localizationData), null, true)
                  .format('MMMM D')}
              >
                {dayjs(day).date()}
              </Text>
              {!props.dayBeforeDeparture && (
                <SkeletonCalendarCard
                  showExtendedLayout={true}
                  dayCardDaySkeletonStyle={dayCardDaySkeletonStyle}
                />
              )}
            </>
          );
        }
      }
    }
    return (
      <>
        {!isFocused &&
          !isOutside &&
          !isPastDate(day) &&
          !isDisregarded &&
          data &&
          data.dataType &&
          data.dataType !== 'DATA_API' &&
          !showDayEmpty && (
            <Tooltip
              id={`dayCard-tooltip-${dayjs(day).format('YYYY-MM-DD')}`}
              {...dayCardTooltipSettings}
            >
              <Box>{translate('day-card-tooltip-info', 'Click to view more fares')}</Box>
            </Tooltip>
          )}
        {!isOutside && (
          <>
            {' '}
            {props.isBlackoutDay ? (
              <>
                {!isDisregarded && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    alignItems="center"
                    textAlign="center"
                    flex="1"
                    {...dayCardMobile.blackoutDayStyles}
                  >
                    {renderDayAtom(day, dayCardMobile)}
                    <Icon
                      icon="close-material"
                      size="20"
                      {...dayCardMobile.blackoutDayStyles.iconStyles}
                    />
                  </Box>
                )}
                {isDisregarded && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    alignItems="center"
                    textAlign="center"
                    flex="1"
                    {...dayCardMobile.blackoutDisregardedDayStyles}
                  >
                    {dayCardMobile.showDay && (
                      <Text
                        {...dayCardMobile.blackoutDisregardedDayStyles?.dayCardMobileDay}
                        role="heading"
                        ariaLevel="3"
                        ariaLabel={dayjs(day)
                          .locale(
                            getRelativeDateLocalization(translate, localizationData),
                            null,
                            true
                          )
                          .format('MMMM D')}
                      >
                        {dayjs(day).date()}
                      </Text>
                    )}
                  </Box>
                )}
              </>
            ) : showAsEmptyDay ? (
              <>
                {dayCardMobile.showDay && (
                  <Text
                    {...dayCardMobile.dayCardMobileDayEmpty}
                    role="heading"
                    ariaLevel="3"
                    ariaLabel={dayjs(day)
                      .locale(getRelativeDateLocalization(translate, localizationData), null, true)
                      .format('MMMM D')}
                  >
                    {dayjs(day).date()}
                  </Text>
                )}
              </>
            ) : (
              <>
                {' '}
                {!isOutside && (
                  <>
                    <Box {...cardContentSettings}>
                      {renderDayAtom(day, dayCardMobile)}
                      {!showDayEmpty && isLowest && !isPastDate(day) && highlightLowestPrices && (
                        <Box textAlign="center" {...dayCardMobile.lowestRowStyle}>
                          <Box
                            style={{
                              background: '#00855C',
                              height: '8px',
                              width: '8px',
                              '-moz-border-radius': '50%',
                              '-webkit-border-radius': '50%',
                              'border-radius': '50%'
                            }}
                            {...lowestCircleStyle}
                          ></Box>
                        </Box>
                      )}
                    </Box>
                    {!showDayEmpty && !isPastDate(day) && !isDisregarded && (
                      <>
                        {data && data.dataType && data.dataType === 'DATA_API' && (
                          <Box {...dayCardMobile.priceContainer}>
                            {showPrice && (
                              <FareItemPrice
                                {...data}
                                {...dayCardMobile.dayCardMobilePrice}
                                showPricePromo={false}
                              />
                            )}
                            {showMiles && (
                              <FareItemMiles
                                {...data}
                                {...dayCardMobile.dayCardMobileMiles}
                                showMilesPromo={false}
                              />
                            )}
                          </Box>
                        )}
                        {((data && data.dataType && data.dataType !== 'DATA_API') ||
                          (data === null && (dayjs(day).get('month') === monthShow || loading)) ||
                          (isDisregarded && disregardedBehavior === 'glass-date')) && (
                          <FareItemNoPriceAvailable
                            showMessage={false}
                            {...dayCardMobile.dayCardMobileNoPriceAvailable}
                            handleClick={handleCallToAction}
                            onKeyDown={handleCallToActionKeyDown}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  };

  let classNameValue = '';
  if (
    !loading &&
    !isBlackoutDay &&
    !isPastDate(day) &&
    !isDisregarded &&
    !isOutside &&
    !props.isEmptyCard
  ) {
    if (isToday) {
      classNameValue = 'fr-today-date-hover';
    } else if (selectedStartDate) {
      classNameValue = 'fr-start-date-hover';
    } else if (selectedEndDate) {
      classNameValue = 'fr-end-date-hover';
    } else if (isInDateRange) {
      classNameValue = 'fr-in-range-date-hover';
    } else {
      classNameValue = 'fr-day-hover';
    }
  }
  if (isOutside) {
    classNameValue = `${classNameValue} fr-day-outside`;
  }
  if (isPastDate(day)) {
    classNameValue = `${classNameValue} fr-day-past`;
  }
  if (isInDateRange && isBlackoutDay && !loading) {
    classNameValue = `${classNameValue} fr-day-in-range-and-blackout`;
  }
  if (loading) {
    if (selectedStartDate || selectedEndDate) {
      classNameValue = `${classNameValue} fr-day-selected-loading`;
    } else {
      classNameValue = `${classNameValue} fr-day-no-selected-loading`;
    }
  } else {
    if (selectedStartDate) {
      classNameValue = `${classNameValue} fr-day-start-selected`;
    } else {
      classNameValue = `${classNameValue} fr-day-start-not-selected`;
    }
    if (selectedEndDate) {
      classNameValue = `${classNameValue} fr-day-end-selected`;
    } else {
      classNameValue = `${classNameValue} fr-day-end-not-selected`;
    }
  }

  if (!isFocused) {
    classNameValue = `${classNameValue} fr-day-mobile-not-focused`;
  } else {
    if (!isOutside && !isPastDate(day) && !isDisregarded && !loading) {
      classNameValue = `${classNameValue} fr-day-focused`;
    }
  }
  if (!loading) {
    classNameValue = `${classNameValue} fr-day-not-loading`;
  }

  let wrapperStyleSettings = {};
  if (!loading) {
    if (confirmedDate) {
      if (selectedStartDate && !sameDepartureAndReturnDate) {
        if (direction === 'ltr') {
          wrapperStyleSettings = {
            ...departureDateLtrStyle
          };
        } else {
          wrapperStyleSettings = {
            ...departureDateRtlStyle
          };
        }
      } else if (selectedEndDate && !sameDepartureAndReturnDate) {
        if (direction === 'ltr') {
          wrapperStyleSettings = {
            ...returnDateLtrStyle
          };
        } else {
          wrapperStyleSettings = {
            ...returnDateRtlStyle
          };
        }
      }
    } else {
      if (highlightDeparture) {
        if (direction === 'ltr') {
          wrapperStyleSettings = {
            ...departureDateLtrStyle
          };
        } else {
          wrapperStyleSettings = {
            ...departureDateRtlStyle
          };
        }
      } else if ((isInDateRange && islastHoveredDate) || selectedEndDate) {
        if (direction === 'ltr') {
          wrapperStyleSettings = {
            ...hoverLastDateLtrStyle
          };
        } else {
          wrapperStyleSettings = {
            ...hoverLastDateRtlStyle
          };
        }
      }
    }
  }

  let classNameBox = wrapperStyleSettings?.className ? wrapperStyleSettings?.className : '';
  if (selectedDay) {
    classNameBox = `${classNameBox} fr-day-selected-container`;
  }
  if (!selectedStartDate && !selectedEndDate && isInDateRange && !islastHoveredDate) {
    classNameBox = `${classNameBox} fr-day-card-hover`;
  }

  if (!loading && isOneWayTrip && isSelectedStartDate) {
    classNameBox = `${classNameBox} fr-selected-day`;
  }

  const dayCardRef = React.useRef(null);

  React.useEffect(() => {
    if (accessibleByArrowKeys && focusDate) {
      if (
        dayjs(focusDate).isSame(day, 'day') &&
        props.avoidFocusDateAction !== undefined &&
        !props.avoidFocusDateAction &&
        !isOutside
      ) {
        dayCardRef?.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate]);

  const cardRender = (
    <Box style={{ width: '100%' }} {...wrapperStyleSettings} className={classNameBox}>
      <Box
        display="flex"
        flexDirection="column"
        paddingHeight="2"
        alignItems="center"
        {...dayCardMobile}
        className={classNameValue}
        role={'gridcell'}
        tabIndex={isInteractive ? 0 : -1}
        focusReferences={accessibleByArrowKeys ? dayCardRef : focusReferences}
        focusable={tabIndex}
        onClick={(e) => {
          //return enabledCallToAction ? handleClick() : handlerClick(day);
          if (loading) {
            return;
          }
          return handlerDayClick(day, e);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          setIsPressed(false);
        }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseMove={(e) => handlerDayHover(day, e)}
        onKeyDown={(e) => {
          if (e.keyCode === KEY_ENTER || e.keyCode === KEY_SPACE) {
            handlerDayClick(day, e);
          } else if (handleKeyboardNavigation) {
            handleKeyboardNavigation(day, e);
          }
        }}
        dataTestId={dataTestId}
        id={`day-${dayjs(day).format('YYYY-MM-DD')}`}
        dataToolTipId={
          !isOutside &&
          !isPastDate(day) &&
          data &&
          data.dataType &&
          data.dataType !== 'DATA_API' &&
          !showDayEmpty
            ? `dayCard-tooltip-${dayjs(day).format('YYYY-MM-DD')}`
            : undefined
        }
      >
        <Text
          className="fr-sr-only"
          ariaLabel={
            isLowest ? translate('cmp-day-card-lowest-price', 'Lowest price of the month') : null
          }
        />
        {renderCardContent()}
      </Box>
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{cardRender}</ErrorBoundary> : { cardRender };
};

DayCardMobile.propTypes = {
  /**
   * Cursor type
   */
  cursorType: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * The day to render
   */
  day: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the day has the lowest price
   */
  isLowest: PropTypes.bool,
  /**
   * Determine if the day is outside.
   */
  isOutside: PropTypes.bool,
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobileDay
   */
  dayCardMobileDay: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobilePrice
   */
  dayCardMobilePrice: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobileNoPriceAvailable
   */
  dayCardMobileNoPriceAvailable: PropTypes.objectOf(PropTypes.any),
  /**
   * Turn off or on the showPrice section
   */
  showPrice: PropTypes.bool,
  /**
   * Turn off or on the showMiles section
   */
  showMiles: PropTypes.bool,
  /**
   * Turn off or on the showDay section
   */
  showDay: PropTypes.bool,
  /**
   * Event fired when key down element
   */
  handleDayKeyDown: PropTypes.func,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * Props of currentDayStyles
   */
  currentDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of lowestPriceStyles
   */
  lowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of pastDayStyles
   */
  pastDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of outsideDayStyles
   */
  outsideDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Props of noPriceAvailableStyles
   */
  noPriceAvailableStyles: PropTypes.objectOf(PropTypes.any),
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date']),
  isDisregarded: PropTypes.bool
};

DayCardMobile.defaultProps = {
  onClick: () => {},
  cursorType: 'pointer',
  translate: () => {},
  day: null,
  isLowest: false,
  isOutside: false,
  data: null,
  dayCardMobileDay: null,
  dayCardMobilePrice: null,
  dayCardMobileNoPriceAvailable: null,
  showPrice: true,
  showMiles: false,
  showDay: true,
  handleDayKeyDown: () => {},
  loading: false,
  usedErrorBoundary: true,
  interactionHotZone: 'smart',
  currentDayStyles: {
    borderColor: 'primary',
    border: true
  },
  pastDayStyles: {
    disabled: true,
    bgColor: 'light',
    textColor: 'dark-6'
  },
  outsideDayStyles: {
    showDay: false,
    bgColor: 'light',
    cursor: 'default'
  },
  lowestPriceStyles: {
    bgColor: 'lowestFareBg',
    textColor: 'lowestFareText',
    fontWeight: 'bold'
  },
  noPriceAvailableStyles: {},
  disregardedBehavior: 'past-date',
  isDisregarded: false
};

export default DayCardMobile;
